import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { normalizePro20PageResponse } from '@/shared/api/normalize-pro20-page';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type { Pro20ApiResponse } from '@/shared/types/api-response.types';
import type { PaginatedResponse } from '@/shared/types/api.types';

import type {
  CreateFormMenuDateParams,
  CreateFormMenuParams,
  DeleteFormMenuDateParams,
  DeleteFormMenuParams,
  FormMenuDateRange,
  FormMenuItem,
  FormMenusCalendarMap,
  GetFormMenuByCodeParams,
  GetFormMenuDateParams,
  GetFormMenusAvailableParams,
  GetFormMenusByBranchParams,
  GetFormMenusCalendarParams,
  RemoveFormMenuImageParams,
  UpdateFormMenuParams,
  UpdateFormMenuStatusParams,
  UploadFormMenuImageParams,
  UploadFormMenuImageResponse,
} from '../types/form-menu.types';
import {
  normalizeFormMenu,
  normalizeFormMenuDate,
  normalizeFormMenusCalendar,
  normalizeFormMenusList,
  normalizeUploadFormMenuImageResponse,
} from '../utils/normalize-form-menu';

function buildFormMenusListUrl({
  branchCode,
  page = 0,
  size = 20,
  search,
  sortBy = 'name',
  sortDirection = 'asc',
}: GetFormMenusByBranchParams): string {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('size', String(size));
  params.set('sortBy', sortBy);
  params.set('sortDirection', sortDirection);
  params.set('search', search?.trim() ?? '');
  return `/branches/${branchCode}/form-menus?${params.toString()}`;
}

function requireFormMenu(raw: unknown, label: string): FormMenuItem {
  const normalized = normalizeFormMenu(raw);
  if (!normalized) {
    throw new Error(`Invalid ${label} response`);
  }
  return normalized;
}

export const formMenuApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFormMenusByBranch: builder.query<
      PaginatedResponse<FormMenuItem>,
      GetFormMenusByBranchParams
    >({
      query: (args) => ({
        url: buildFormMenusListUrl(args),
        method: 'GET',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>, _meta, arg) =>
        normalizePro20PageResponse(
          normalizePro20Response(response),
          normalizeFormMenu,
          arg.size ?? 20,
        ),
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.FormMenus, id: branchCode },
      ],
    }),

    getFormMenusCalendar: builder.query<
      FormMenusCalendarMap,
      GetFormMenusCalendarParams
    >({
      query: ({ branchCode, formCode, startDate, endDate }) => {
        const params = new URLSearchParams();
        params.set('startDate', startDate);
        params.set('endDate', endDate);
        params.set('formCode', formCode);
        return {
          url: `/branches/${branchCode}/form-menus/calendar?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizeFormMenusCalendar(normalizePro20Response(response)),
      providesTags: (_result, _error, { branchCode, formCode }) => [
        { type: API_TAGS.FormMenus, id: `${branchCode}-calendar-${formCode}` },
      ],
    }),

    getFormMenusAvailable: builder.query<
      FormMenuItem[],
      GetFormMenusAvailableParams
    >({
      query: ({ formCode, date }) => {
        const params = new URLSearchParams();
        params.set('formCode', formCode);
        params.set('date', date);
        return {
          url: `/form-menus/available?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizeFormMenusList(normalizePro20Response(response)),
      providesTags: (_result, _error, { formCode, date }) => [
        { type: API_TAGS.FormMenus, id: `available-${formCode}-${date}` },
      ],
    }),

    getFormMenuByCode: builder.query<FormMenuItem, GetFormMenuByCodeParams>({
      query: ({ branchCode, formMenuCode }) => ({
        url: `/branches/${branchCode}/form-menus/${formMenuCode}`,
        method: 'GET',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        requireFormMenu(normalizePro20Response(response), 'get form menu'),
      providesTags: (_result, _error, { formMenuCode }) => [
        { type: API_TAGS.FormMenus, id: formMenuCode },
      ],
    }),

    getFormMenuDate: builder.query<FormMenuDateRange, GetFormMenuDateParams>({
      query: ({ branchCode, formMenuCode, formMenuDateId }) => ({
        url: `/branches/${branchCode}/form-menus/${formMenuCode}/dates/${String(formMenuDateId)}`,
        method: 'GET',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) => {
        const normalized = normalizeFormMenuDate(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid get form menu date response');
        }
        return normalized;
      },
    }),

    createFormMenu: builder.mutation<FormMenuItem, CreateFormMenuParams>({
      query: ({ branchCode, body }) => ({
        url: `/branches/${branchCode}/form-menus`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        requireFormMenu(normalizePro20Response(response), 'create form menu'),
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.FormMenus, id: branchCode },
      ],
    }),

    updateFormMenu: builder.mutation<FormMenuItem, UpdateFormMenuParams>({
      query: ({ branchCode, formMenuCode, body }) => ({
        url: `/branches/${branchCode}/form-menus/${formMenuCode}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        requireFormMenu(normalizePro20Response(response), 'update form menu'),
      invalidatesTags: (_result, _error, { branchCode, formMenuCode }) => [
        { type: API_TAGS.FormMenus, id: branchCode },
        { type: API_TAGS.FormMenus, id: formMenuCode },
      ],
    }),

    updateFormMenuStatus: builder.mutation<
      FormMenuItem,
      UpdateFormMenuStatusParams
    >({
      query: ({ branchCode, formMenuCode, body }) => ({
        url: `/branches/${branchCode}/form-menus/${formMenuCode}/status`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        requireFormMenu(
          normalizePro20Response(response),
          'update form menu status',
        ),
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.FormMenus, id: branchCode },
      ],
    }),

    deleteFormMenu: builder.mutation<null, DeleteFormMenuParams>({
      query: ({ branchCode, formMenuCode }) => ({
        url: `/branches/${branchCode}/form-menus/${formMenuCode}`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown> | null) => {
        if (response) {
          normalizePro20Response(response);
        }
        return null;
      },
      // List refetch is triggered manually after the delete modal closes.
    }),

    duplicateFormMenu: builder.mutation<FormMenuItem, DeleteFormMenuParams>({
      query: ({ branchCode, formMenuCode }) => ({
        url: `/branches/${branchCode}/form-menus/${formMenuCode}/duplicate`,
        method: 'PUT',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        requireFormMenu(normalizePro20Response(response), 'duplicate form menu'),
      invalidatesTags: (_result, _error, { branchCode }) =>
        branchCode ? [{ type: API_TAGS.FormMenus, id: branchCode }] : [],
    }),

    createFormMenuDate: builder.mutation<
      FormMenuItem,
      CreateFormMenuDateParams
    >({
      query: ({ branchCode, formMenuCode, body }) => ({
        url: `/branches/${branchCode}/form-menus/${formMenuCode}/dates`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        requireFormMenu(
          normalizePro20Response(response),
          'create form menu date',
        ),
      invalidatesTags: (_result, _error, { formMenuCode }) => [
        { type: API_TAGS.FormMenus, id: formMenuCode },
      ],
    }),

    deleteFormMenuDate: builder.mutation<
      FormMenuItem,
      DeleteFormMenuDateParams
    >({
      query: ({ branchCode, formMenuCode, formMenuDateId }) => ({
        url: `/branches/${branchCode}/form-menus/${formMenuCode}/dates/${String(formMenuDateId)}`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        requireFormMenu(
          normalizePro20Response(response),
          'delete form menu date',
        ),
      invalidatesTags: (_result, _error, { formMenuCode }) => [
        { type: API_TAGS.FormMenus, id: formMenuCode },
      ],
    }),

    uploadFormMenuImage: builder.mutation<
      UploadFormMenuImageResponse,
      UploadFormMenuImageParams
    >({
      query: ({ branchCode, formMenuCode, formData }) => ({
        url: `/branches/${branchCode}/form-menu/upload/${formMenuCode}/image`,
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizeUploadFormMenuImageResponse(normalizePro20Response(response)),
    }),

    removeFormMenuImage: builder.mutation<null, RemoveFormMenuImageParams>({
      query: ({ branchCode, formMenuCode }) => ({
        url: `/branches/${branchCode}/form-menu/remove/${formMenuCode}/image`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown> | null) => {
        if (response) {
          normalizePro20Response(response);
        }
        return null;
      },
    }),
  }),
});

export const {
  useGetFormMenusByBranchQuery,
  useGetFormMenusCalendarQuery,
  useGetFormMenusAvailableQuery,
  useGetFormMenuByCodeQuery,
  useLazyGetFormMenuDateQuery,
  useCreateFormMenuMutation,
  useUpdateFormMenuMutation,
  useUpdateFormMenuStatusMutation,
  useDeleteFormMenuMutation,
  useDuplicateFormMenuMutation,
  useCreateFormMenuDateMutation,
  useDeleteFormMenuDateMutation,
  useUploadFormMenuImageMutation,
  useRemoveFormMenuImageMutation,
} = formMenuApi;
